import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type ChangeEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { isoToDisplay, nameParts, parseDisplayDate } from '@/utils/format';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarDays, Check, CheckCircle2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, LayoutGrid, List, Loader2, RotateCcw, Rows3, Search, X } from 'lucide-react';

/* ---------------- Buttons ---------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md';
  icon?: ReactNode;
  loading?: boolean;
  iconOnly?: boolean;
};

export function Button({ variant = 'secondary', size = 'md', icon, loading, iconOnly, children, className = '', disabled, ...rest }: ButtonProps) {
  const cls = ['btn', variant !== 'secondary' ? variant : '', size === 'sm' ? 'sm' : '', iconOnly ? 'icon' : '', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={disabled || loading} {...rest}>
      {loading ? <Loader2 size={16} className="spin" /> : icon}
      {children}
    </button>
  );
}

/* ---------------- Form fields ---------------- */

export function Field({
  label,
  error,
  hint,
  required,
  children,
  className = '',
  htmlFor,
}: {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <div className={`field ${error ? 'invalid' : ''} ${className}`}>
      {label && (
        <label htmlFor={htmlFor}>
          {label}
          {required && <span className="req">*</span>}
        </label>
      )}
      {children}
      {error ? <span className="error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; error?: string; hint?: ReactNode; fieldClassName?: string };

export function TextInput({ label, error, hint, required, fieldClassName, id, ...rest }: InputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const isDate = rest.type === 'date' || rest.type === 'datetime-local';
  return (
    <Field label={label} error={error} hint={hint} required={required} className={fieldClassName} htmlFor={inputId}>
      {isDate ? (
        <DateField id={inputId} withTime={rest.type === 'datetime-local'} required={required} invalid={!!error} {...rest} />
      ) : (
        <input id={inputId} className="input" required={required} aria-invalid={!!error} {...rest} />
      )}
    </Field>
  );
}

/**
 * Date (dd/MM/yyyy) or date-time (dd/MM/yyyy + HH:mm) input that looks the
 * same in every browser and language. Emits ISO values ("yyyy-MM-dd" or
 * "yyyy-MM-ddTHH:mm") through the usual onChange(e.target.value).
 */
export function DateInput(props: InputHTMLAttributes<HTMLInputElement> & { withTime?: boolean; invalid?: boolean }) {
  return (
    <div style={{ width: props.withTime ? 260 : 170, ...(props.style ?? {}) }}>
      <DateField {...props} style={undefined} />
    </div>
  );
}

function DateField({
  id,
  value,
  onChange,
  withTime,
  required,
  invalid,
  disabled,
  min,
  max,
  'aria-label': ariaLabel,
}: InputHTMLAttributes<HTMLInputElement> & { withTime?: boolean; invalid?: boolean }) {
  const { t } = useTranslation();
  const iso = String(value ?? '');
  const [datePart, timePart = ''] = withTime ? iso.split('T') : [iso];
  const [text, setText] = useState(isoToDisplay(datePart));
  const [time, setTime] = useState(timePart.slice(0, 5));
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setText(isoToDisplay(datePart));
    setTime(timePart.slice(0, 5));
  }, [datePart, timePart]);
  const emit = (d: string, tm: string) =>
    onChange?.({ target: { value: d ? (withTime ? `${d}T${tm || '00:00'}` : d) : '' } } as unknown as ChangeEvent<HTMLInputElement>);
  const onText = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, 8);
    const masked = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/');
    const next = raw.length < text.length ? raw : masked; // keep deletes natural
    setText(next);
    const parsed = parseDisplayDate(next);
    if (parsed) emit(parsed, time);
    else if (!next) emit('', time);
  };
  const onTime = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, 4);
    const next = raw.length < time.length ? raw : digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    setTime(next);
    const m = next.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    const d = parseDisplayDate(text);
    if (m && d) emit(d, next);
  };
  return (
    <div className={`date-field ${invalid ? 'invalid' : ''}`}>
      <input
        id={id}
        className="input"
        inputMode="numeric"
        placeholder={t('common.datePlaceholder')}
        aria-label={ariaLabel}
        value={text}
        required={required}
        disabled={disabled}
        aria-invalid={invalid}
        onChange={(e) => onText(e.target.value)}
        onBlur={() => {
          if (text && !parseDisplayDate(text)) setText(isoToDisplay(datePart));
        }}
      />
      {withTime && (
        <input
          className="input date-field-time"
          inputMode="numeric"
          placeholder="HH:mm"
          aria-label={t('common.time')}
          value={time}
          disabled={disabled}
          onChange={(e) => onTime(e.target.value)}
          onBlur={() => {
            if (time && !/^([01]\d|2[0-3]):([0-5]\d)$/.test(time)) setTime(timePart.slice(0, 5));
          }}
        />
      )}
      <button
        type="button"
        className="date-field-btn"
        disabled={disabled}
        aria-label={t('common.pickDate')}
        title={t('common.pickDate')}
        onClick={() => {
          const el = picker.current;
          if (!el) return;
          try {
            el.showPicker();
          } catch {
            el.focus();
          }
        }}
      >
        <CalendarDays size={16} />
      </button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="date-field-native"
        value={/^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : ''}
        min={typeof min === 'string' ? min.slice(0, 10) : undefined}
        max={typeof max === 'string' ? max.slice(0, 10) : undefined}
        onChange={(e) => {
          setText(isoToDisplay(e.target.value));
          emit(e.target.value, time || (withTime ? '09:00' : ''));
        }}
      />
    </div>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; error?: string; hint?: ReactNode; fieldClassName?: string };

export function TextArea({ label, error, hint, required, fieldClassName, id, ...rest }: TextareaProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} error={error} hint={hint} required={required} className={fieldClassName} htmlFor={inputId}>
      <textarea id={inputId} className="textarea" required={required} aria-invalid={!!error} {...rest} />
    </Field>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  options: { value: string; label: string }[];
  placeholder?: string;
  fieldClassName?: string;
};

export function SelectInput({ label, error, hint, required, options, placeholder, fieldClassName, id, ...rest }: SelectProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} error={error} hint={hint} required={required} className={fieldClassName} htmlFor={inputId}>
      <select id={inputId} className="select" required={required} aria-invalid={!!error} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function Checkbox({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  delay = 300,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  delay?: number;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState(value);
  const { t } = useTranslation();
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const h = setTimeout(() => onChange(text), delay);
    return () => clearTimeout(h);
  }, [text, value, delay, onChange]);
  return (
    <div className="input-group" style={{ flex: 1, minWidth: 200 }}>
      <Search size={16} />
      <input
        className="input"
        type="search"
        value={text}
        autoFocus={autoFocus}
        placeholder={placeholder ?? t('common.searchPlaceholder')}
        aria-label={placeholder ?? t('common.search')}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
}

/* ---------------- Containers ---------------- */

export function Card({
  title,
  actions,
  children,
  flush,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-header">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={`card-body ${flush ? 'flush' : ''}`}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { value: T; label: ReactNode; hidden?: boolean }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs
        .filter((t) => !t.hidden)
        .map((tab) => (
          <button
            key={tab.value}
            role="tab"
            aria-selected={value === tab.value}
            className={`tab ${value === tab.value ? 'active' : ''}`}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="segmented" role="radiogroup">
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={o.value === value ? 'active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary'; children: ReactNode }) {
  return <span className={`badge ${tone === 'neutral' ? '' : tone}`}>{children}</span>;
}

const STATUS_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary'> = {
  ACTIVE: 'success',
  COMPLETED: 'success',
  PAID: 'success',
  VERIFIED: 'success',
  DISPENSED: 'success',
  SERVED: 'success',
  SIGNED: 'success',
  CONFIRMED: 'info',
  BOOKED: 'primary',
  ISSUED: 'warning',
  ORDERED: 'warning',
  WAITING: 'warning',
  PENDING: 'warning',
  PARTIALLY_PAID: 'warning',
  PARTIALLY_DISPENSED: 'warning',
  PRELIMINARY: 'warning',
  RESULTED: 'info',
  COLLECTED: 'info',
  CHECKED_IN: 'info',
  CALLED: 'info',
  SERVING: 'primary',
  IN_PROGRESS: 'primary',
  DRAFT: 'neutral',
  CANCELLED: 'neutral',
  VOID: 'neutral',
  NO_SHOW: 'danger',
  FAILED: 'danger',
  REFUNDED: 'neutral',
  DISABLED: 'danger',
  LOCKED: 'danger',
  AMENDED: 'neutral',
  CORRECTED: 'neutral',
  SKIPPED: 'neutral',
  EMERGENCY: 'danger',
  STAT: 'danger',
  URGENT: 'warning',
  SEVERE: 'danger',
  LIFE_THREATENING: 'danger',
  MODERATE: 'warning',
  MILD: 'info',
  CRITICAL: 'danger',
  WARNING: 'warning',
  INFO: 'info',
  H: 'warning',
  L: 'warning',
  HH: 'danger',
  LL: 'danger',
  A: 'warning',
  N: 'success',
  PLANNED: 'info',
  SCHEDULED: 'primary',
  MISSED: 'danger',
  OVERDUE: 'danger',
  STOPPED: 'neutral',
  NORMAL: 'success',
  ELEVATED: 'warning',
  LOW: 'warning',
  PENDING_VERIFICATION: 'warning',
  OPEN: 'success',
  PAUSED: 'warning',
  CLOSED: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  SENT: 'primary',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  IN_SERVICE: 'success',
  UNDER_MAINTENANCE: 'warning',
  OUT_OF_SERVICE: 'danger',
  DISPOSED: 'neutral',
  OUT: 'danger',
  OK: 'success',
  OVER: 'info',
  ASSET: 'neutral',
  EXPIRED: 'danger',
  EXPIRING: 'warning',
  AVAILABLE: 'success',
  QUARANTINE: 'warning',
  RECEIPT: 'success',
  OPENING: 'info',
  DISPENSE: 'primary',
  ISSUE: 'primary',
  TRANSFER_IN: 'info',
  TRANSFER_OUT: 'info',
  ADJUSTMENT_IN: 'info',
  ADJUSTMENT_OUT: 'warning',
  WRITE_OFF: 'danger',
  RETURN_TO_SUPPLIER: 'warning',
  SALE: 'primary',
  SALE_RETURN: 'info',
  POSTED: 'success',
  ADMITTED: 'primary',
  DISCHARGED: 'success',
  OCCUPIED: 'primary',
  CLEANING: 'warning',
  RESERVED: 'info',
  REPORTED: 'warning',
  VOIDED: 'neutral',
  RETURNED: 'neutral',
  PARTIALLY_RETURNED: 'warning',
  IN_TRANSIT: 'info',
  PREPARING: 'primary',
  PUBLISHED: 'success',
  HIDDEN: 'neutral',
  ACHIEVED: 'success',
  INSUFFICIENT_DATA: 'neutral',
  NOT_APPLICABLE: 'neutral',
  INCOMPLETE: 'warning',
  INVALID: 'danger',
  VALIDATED: 'info',
  SUPERSEDED: 'neutral',
  INVALIDATED: 'danger',
  RETIRED: 'neutral',
  QUEUED: 'warning',
  SUBMITTING: 'primary',
  ACCEPTED: 'success',
  PARTIALLY_ACCEPTED: 'warning',
  RETRY_PENDING: 'warning',
  RECONCILIATION_REQUIRED: 'danger',
  IGNORED: 'warning',
  SUSPENDED: 'danger',
  UNREACHABLE: 'danger',
  DECLINED: 'danger',
  INACTIVE: 'neutral',
  TRANSFERRED: 'info',
};

/** Status chip translated through `enums.<group>.<value>`. */
export function StatusBadge({ group, value }: { group: string; value: string | null | undefined }) {
  const { t } = useTranslation();
  if (!value) return <span className="muted">—</span>;
  return <Badge tone={STATUS_TONES[value] ?? 'neutral'}>{t(`enums.${group}.${value}`, { defaultValue: value })}</Badge>;
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="empty" role="status">
      <Loader2 className="spin" size={22} />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <div>{children}</div>
    </div>
  );
}

export function Alert({ tone = 'info', children }: { tone?: 'info' | 'danger' | 'warning' | 'success'; children: ReactNode }) {
  const Icon = tone === 'danger' || tone === 'warning' ? AlertTriangle : tone === 'success' ? CheckCircle2 : Info;
  return (
    <div className={`alert ${tone === 'info' ? '' : tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon size={18} style={{ flex: 'none', marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="stack" style={{ padding: 16 }}>
      <Alert tone="danger">{error}</Alert>
      {onRetry && (
        <div>
          <Button icon={<RotateCcw size={16} />} onClick={onRetry}>{t('common.retry')}</Button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Modal ---------------- */

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  size,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'lg' | 'xl';
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size ?? ''}`} role="dialog" aria-modal="true" ref={ref}>
        <div className="modal-header">
          <h2>{title}</h2>
          <Button variant="ghost" iconOnly size="sm" aria-label={t('common.close')} onClick={onClose} icon={<X size={18} />} />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/** Confirmation, optionally collecting a mandatory reason. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  danger,
  reasonLabel,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: ReactNode;
  danger?: boolean;
  reasonLabel?: string;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button icon={<X size={16} />} onClick={onClose}>{t('common.cancel')}</Button>
          <Button icon={<Check size={16} />}
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={!!reasonLabel && !reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {message && <p>{message}</p>}
        {reasonLabel && <TextArea label={reasonLabel} required value={reason} onChange={(e) => setReason(e.target.value)} />}
      </div>
    </Modal>
  );
}

/* ---------------- Table ---------------- */

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
  primary?: boolean;
}

export type ViewMode = 'list' | 'cards' | 'grid';

/** Remembered per list (browser storage; falls back to the default). */
export function useViewMode(key: string | undefined, fallback: ViewMode = 'list') {
  const storageKey = key ? `hms.view.${key}` : '';
  const [mode, setMode] = useState<ViewMode>(() => {
    if (!storageKey) return fallback;
    try {
      const v = localStorage.getItem(storageKey);
      return v === 'list' || v === 'cards' || v === 'grid' ? v : fallback;
    } catch {
      return fallback;
    }
  });
  const update = (m: ViewMode) => {
    setMode(m);
    try {
      if (storageKey) localStorage.setItem(storageKey, m);
    } catch {
      /* storage unavailable: keep in memory */
    }
  };
  return [mode, update] as const;
}

/** List / Cards / Grid toggle. */
export function ViewSwitcher({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const { t } = useTranslation();
  const opts: { v: ViewMode; icon: ReactNode }[] = [
    { v: 'list', icon: <List size={16} /> },
    { v: 'cards', icon: <Rows3 size={16} /> },
    { v: 'grid', icon: <LayoutGrid size={16} /> },
  ];
  return (
    <div className="segmented view-switcher" role="radiogroup" aria-label={t('common.viewAs')}>
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          className={value === o.v ? 'active' : ''}
          title={t(`common.views.${o.v}`)}
          aria-label={t(`common.views.${o.v}`)}
          onClick={() => onChange(o.v)}
        >
          {o.icon}
          <span className="view-label">{t(`common.views.${o.v}`)}</span>
        </button>
      ))}
    </div>
  );
}

/** Card built from the table columns: primary column as title, others as label/value. */
function AutoCard<T>({ row, columns, compact }: { row: T; columns: Column<T>[]; compact?: boolean }) {
  const primary = columns.find((c) => c.primary) ?? columns[0];
  const fields = columns.filter((c) => c !== primary && typeof c.header === 'string' && c.header !== '');
  const actions = columns.filter((c) => c !== primary && (c.header === '' || c.header == null));
  const shown = compact ? fields.slice(0, 4) : fields;
  return (
    <>
      <div className="item-card-title">{primary.render(row)}</div>
      <dl className="item-card-fields">
        {shown.map((c) => (
          <div key={c.key}>
            <dt>{c.header}</dt>
            <dd>{c.render(row)}</dd>
          </div>
        ))}
      </dl>
      {actions.length > 0 && (
        <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
          {actions.map((c) => (
            <span key={c.key}>{c.render(row)}</span>
          ))}
        </div>
      )}
    </>
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  viewKey,
  card,
  toolbar,
  defaultView,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** Enables the List / Cards / Grid switcher, remembered under this key. */
  viewKey?: string;
  /** Custom card body for the Cards / Grid views. */
  card?: (row: T) => ReactNode;
  /** Extra controls shown left of the view switcher. */
  toolbar?: ReactNode;
  defaultView?: ViewMode;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useViewMode(viewKey, defaultView);
  const bar = (viewKey || toolbar) && (
    <div className="table-toolbar">
      <div className="row">{toolbar}</div>
      {viewKey && <ViewSwitcher value={mode} onChange={setMode} />}
    </div>
  );
  if (!rows.length)
    return (
      <>
        {bar}
        <EmptyState>{empty ?? t('common.noResults')}</EmptyState>
      </>
    );
  if (viewKey && mode !== 'list')
    return (
      <>
        {bar}
        <div className={mode === 'grid' ? 'item-grid' : 'item-cards'}>
          {rows.map((row) => (
            <div
              key={rowKey(row)}
              className={`item-card ${onRowClick ? 'clickable' : ''}`}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(row) : undefined}
            >
              {card ? card(row) : <AutoCard row={row} columns={columns} compact={mode === 'grid'} />}
            </div>
          ))}
        </div>
      </>
    );
  return (
    <>
      {bar}
      <div className="table-wrap">
        <table className="table responsive">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.className}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'clickable' : ''}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[c.className, c.primary ? 'primary-cell' : ''].filter(Boolean).join(' ')}
                    data-label={typeof c.header === 'string' ? c.header : ''}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Pager with First / Previous / page jump / Next / Last and rows-per-page.
 * Renders nothing for an empty list.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
  onPageSizeChange?: (size: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [jump, setJump] = useState(String(page));
  useEffect(() => setJump(String(page)), [page]);
  if (total === 0 || (total <= pageSize && !onPageSizeChange)) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const go = (p: number) => onChange(Math.min(pages, Math.max(1, p)));
  const commitJump = () => {
    const n = Number(jump);
    if (Number.isFinite(n) && n >= 1) go(Math.round(n));
    else setJump(String(page));
  };
  return (
    <nav className="pager" aria-label={t('common.pagination')}>
      <span className="pager-info">{t('common.showing', { from, to, total })}</span>
      <div className="pager-controls">
        {onPageSizeChange && (
          <label className="pager-size">
            <span>{t('common.rowsPerPage')}</span>
            <select className="select" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="pager-buttons">
          <button type="button" className="pager-btn pager-edge" disabled={page <= 1} onClick={() => go(1)} aria-label={t('common.first')} title={t('common.first')}>
            <ChevronsLeft size={16} />
          </button>
          <button type="button" className="pager-btn" disabled={page <= 1} onClick={() => go(page - 1)} aria-label={t('common.previous')} title={t('common.previous')}>
            <ChevronLeft size={16} />
          </button>
          {pageItems(page, pages).map((it) =>
            typeof it === 'number' ? (
              <button
                key={it}
                type="button"
                className={`pager-btn pager-num ${it === page ? 'active' : ''}`}
                aria-current={it === page ? 'page' : undefined}
                aria-label={t('common.pageOf', { page: it, pages })}
                onClick={() => go(it)}
              >
                {it}
              </button>
            ) : (
              <button
                key={it}
                type="button"
                className="pager-btn pager-gap"
                aria-label={t(it === 'back' ? 'common.jumpBack' : 'common.jumpForward', { count: PAGE_JUMP })}
                title={t(it === 'back' ? 'common.jumpBack' : 'common.jumpForward', { count: PAGE_JUMP })}
                onClick={() => go(it === 'back' ? page - PAGE_JUMP : page + PAGE_JUMP)}
              >
                <span className="dots">…</span>
                {it === 'back' ? <ChevronsLeft size={16} className="arrows" /> : <ChevronsRight size={16} className="arrows" />}
              </button>
            ),
          )}
          <button type="button" className="pager-btn" disabled={page >= pages} onClick={() => go(page + 1)} aria-label={t('common.next')} title={t('common.next')}>
            <ChevronRight size={16} />
          </button>
          <button type="button" className="pager-btn pager-edge" disabled={page >= pages} onClick={() => go(pages)} aria-label={t('common.last')} title={t('common.last')}>
            <ChevronsRight size={16} />
          </button>
        </div>
        {pages > 7 && (
          <label className="pager-page">
            <span>{t('common.goToPage')}</span>
            <input
              className="input"
              inputMode="numeric"
              aria-label={t('common.pageOf', { page, pages })}
              value={jump}
              onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={commitJump}
              onKeyDown={(e) => e.key === 'Enter' && commitJump()}
            />
            <span>/ {pages}</span>
          </label>
        )}
      </div>
    </nav>
  );
}

const PAGE_JUMP = 5;

/**
 * Page numbers to show: all of them up to 7 pages, otherwise always 7 slots —
 * first, last, the current page with its neighbours, and a gap on either side.
 * e.g. 1 2 3 4 5 … 20 · 1 … 9 10 11 … 20 · 1 … 16 17 18 19 20
 */
export function pageItems(page: number, pages: number): (number | 'back' | 'forward')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  let start = Math.max(2, page - 1);
  let end = Math.min(pages - 1, page + 1);
  if (page <= 4) [start, end] = [2, 5];
  if (page >= pages - 3) [start, end] = [pages - 4, pages - 1];
  const items: (number | 'back' | 'forward')[] = [1];
  if (start > 2) items.push('back');
  for (let p = start; p <= end; p++) items.push(p);
  if (end < pages - 1) items.push('forward');
  items.push(pages);
  return items;
}

/** Client-side paging for lists already loaded in full. */
export function useClientPaging<T>(rows: T[], initialSize = 20) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pages);
  const pageRows = rows.slice((current - 1) * pageSize, current * pageSize);
  return {
    pageRows,
    pager: {
      page: current,
      pageSize,
      total: rows.length,
      onChange: setPage,
      onPageSizeChange: (n: number) => {
        setPageSize(n);
        setPage(1);
      },
    },
    reset: () => setPage(1),
  };
}

/** DataTable over a fully-loaded list, paged in the browser. */
export function PagedDataTable<T>(props: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  pageSize?: number;
  viewKey?: string;
  card?: (row: T) => ReactNode;
}) {
  const { pageRows, pager } = useClientPaging(props.rows, props.pageSize ?? 10);
  return (
    <>
      <DataTable
        columns={props.columns}
        rows={pageRows}
        rowKey={props.rowKey}
        onRowClick={props.onRowClick}
        empty={props.empty}
        viewKey={props.viewKey}
        card={props.card}
      />
      <Pagination {...pager} />
    </>
  );
}

/* ---------------- Toasts ---------------- */

type Toast = { id: number; tone: 'info' | 'success' | 'error'; message: ReactNode };
const ToastContext = createContext<(message: ReactNode, tone?: Toast['tone']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: ReactNode, tone: Toast['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { id, tone, message }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.tone}`}>
            {toast.tone === 'error' ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
            <div>{toast.message}</div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ---------------- Misc ---------------- */

export function Avatar({ name, large }: { name: string; large?: boolean }) {
  const initials = nameParts(name)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return <div className={`avatar ${large ? 'lg' : ''}`}>{initials || '?'}</div>;
}

export function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onOutside]);
  return ref;
}
